import { useQuery } from '@tanstack/react-query'
import { useAuth, useOrganization } from '@clerk/clerk-react'
import { addDays } from 'date-fns'

import { AGENT_URL } from '../config/env'
import { fetchJsonWithAuth, requireAuthToken } from '../lib/api'
import type { AvailabilityBlock } from '../features/appointments/types'
import type { FilterCondition } from '../utils/filterUtils'

// The column relay bounds the availability window on.
const WINDOW_FIELD = 'start_time'

/**
 * Search forward when a window has no open slots: retry the same filters
 * with the start_time bounds shifted by `windowDays`, up to `maxWindows`
 * windows in total (the first included). The first non-empty window wins.
 */
export interface OpenAvailabilityLookahead {
  windowDays: number
  maxWindows: number
}

export interface OpenAvailabilityQueryParams {
  // Structured filters serialized as filter[field][operator]=value — relay
  // requires BOTH a start_time >= and <= bound (an unbounded or one-sided
  // range is rejected). No limit/cursor/sort_by here: this endpoint isn't
  // paginated, relay coalesces contiguous open slots into a small number of
  // blocks server-side.
  filters: FilterCondition[]
  lookahead?: OpenAvailabilityLookahead
}

export interface OpenAvailabilityResponse {
  data: AvailabilityBlock[]
  truncated: boolean
}

async function fetchOpenAvailability(
  token: string,
  filters: FilterCondition[]
): Promise<OpenAvailabilityResponse> {
  const search = new URLSearchParams()
  for (const filter of filters) {
    const value = filter.value.trim()
    if (!value) continue
    search.append(`filter[${filter.field}][${filter.operator}]`, value)
  }

  const raw = await fetchJsonWithAuth<{
    data?: unknown
    truncated?: unknown
  }>({
    token,
    url: `${AGENT_URL}/data/timeslots/availability?${search.toString()}`,
    errorMessage: 'Failed to fetch open availability',
  })

  const rawBlocks: unknown[] = Array.isArray(raw?.data) ? raw.data : []
  const data: AvailabilityBlock[] = rawBlocks.map((block: any) => ({
    provider_id: String(block?.provider_id ?? ''),
    provider_name:
      typeof block?.provider_name === 'string' ? block.provider_name : '',
    facility_name:
      typeof block?.facility_name === 'string' ? block.facility_name : null,
    appointment_type:
      typeof block?.appointment_type === 'string'
        ? block.appointment_type
        : null,
    // Relay preserves offsets from timestamptz timeslots; no naive-UTC repair is needed.
    block_start:
      typeof block?.block_start === 'string' ? block.block_start : '',
    block_end: typeof block?.block_end === 'string' ? block.block_end : '',
    appointment_interval_id:
      typeof block?.appointment_interval_id === 'string'
        ? block.appointment_interval_id
        : null,
    available_capacity:
      typeof block?.available_capacity === 'number'
        ? block.available_capacity
        : null,
    timeslot_count:
      typeof block?.timeslot_count === 'number'
        ? block.timeslot_count
        : typeof block?.slot_count === 'number'
          ? block.slot_count
          : 0,
  }))

  return { data, truncated: Boolean(raw?.truncated) }
}

/** Shift the start_time `>=`/`<=` bounds forward by `days`; other filters are untouched. */
export function shiftAvailabilityWindow(
  filters: FilterCondition[],
  days: number
): FilterCondition[] {
  return filters.map((filter) =>
    filter.field === WINDOW_FIELD &&
    (filter.operator === '>=' || filter.operator === '<=')
      ? {
          ...filter,
          value: addDays(new Date(filter.value), days).toISOString(),
        }
      : filter
  )
}

/**
 * Fetch the requested window; with `lookahead`, keep trying the following
 * windows while a window comes back empty. Returns the first non-empty
 * window, or the last one tried.
 */
export async function fetchWithLookahead(
  fetchWindow: (
    filters: FilterCondition[]
  ) => Promise<OpenAvailabilityResponse>,
  { filters, lookahead }: OpenAvailabilityQueryParams
): Promise<OpenAvailabilityResponse> {
  const windows = lookahead ? Math.max(1, lookahead.maxWindows) : 1
  const windowDays = lookahead?.windowDays ?? 0
  let result = await fetchWindow(filters)
  for (let i = 1; i < windows && result.data.length === 0; i++) {
    // eslint-disable-next-line no-await-in-loop -- sequential on purpose: the next window is only needed when this one is empty
    result = await fetchWindow(shiftAvailabilityWindow(filters, windowDays * i))
  }
  return result
}

/**
 * Open provider-timeslot availability for the appointments calendar and
 * table, gated by the caller via `options.enabled`. Unlike
 * `useAppointmentsRangeQuery` there's no cursor walk — relay returns
 * already-coalesced blocks bounded by the requested date window — but a
 * `lookahead` may issue a few sequential window requests in one queryFn.
 */
export function useOpenAvailabilityQuery(
  params: OpenAvailabilityQueryParams,
  options: { enabled?: boolean } = {}
) {
  const { getToken } = useAuth()
  const { organization } = useOrganization()

  return useQuery({
    queryKey: ['timeslots', 'availability', organization?.id, params],
    enabled: options.enabled ?? true,
    queryFn: async () => {
      const token = await requireAuthToken(getToken)
      return fetchWithLookahead(
        (filters) => fetchOpenAvailability(token, filters),
        params
      )
    },
    staleTime: 30_000,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  })
}
